import { Sun, ArrowRight, Mail, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const HERO_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_villa-stays-3/artifacts/ojnub07f_IMG_5297.jpg",
    caption: '"El sol, la familia, y un buen plan."',
  },
  {
    src: "https://customer-assets.emergentagent.com/job_villa-stays-3/artifacts/c4a9xum0_IMG_9104.jpg",
    caption: '"Atardecer en La Manga."',
  },
  {
    src: "https://customer-assets.emergentagent.com/job_villa-stays-3/artifacts/vmy52qoo_IMG_7296%202.jpg",
    caption: '"Fuegos artificiales sobre la casa."',
  },
  {
    src: "https://customer-assets.emergentagent.com/job_villa-stays-3/artifacts/iuiqn4ph_IMG_9525.jpg",
    caption: '"Vista al Mar Menor."',
  },
  {
    src: "https://customer-assets.emergentagent.com/job_villa-stays-3/artifacts/rqclwpj4_IMG_9584.jpg",
    caption: '"Hora dorada — golden hour."',
  },
];

export default function Login() {
  const location = useLocation();
  const error = location.state?.error;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email first");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/magic-link/request", { email: email.trim() });
      setSent(email.trim());
      toast.success("Check your email — link is on its way");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send link");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2" data-testid="login-page">
      {/* Left – content */}
      <div className="flex flex-col justify-between p-8 sm:p-12 villa-grain bg-[#FDFBF7]">
        <div className="flex items-center gap-2 text-[#6B705C]" data-testid="brand-mark">
          <Sun className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-[0.2em]">Casa Blunt</span>
        </div>

        <div className="max-w-xl">
          <p className="text-sm uppercase tracking-[0.25em] text-[#7A7A7A] mb-4">Hola Familia</p>
          <h1 className="font-serif-villa text-5xl sm:text-6xl leading-[1.05] text-[#2B2B2B]">
            Our private<br />
            <span className="italic text-[#C25E3E]">family villa</span>,<br />
            calmly organised.
          </h1>
          <p className="mt-6 text-lg text-[#5A5A5A] leading-relaxed">
            Plan stays, see who's at the casa, request the cleaner, the laundry, and the car —
            all in one quiet place. <em className="text-[#6B705C]">Buenos días.</em>
          </p>

          <button
            onClick={handleGoogle}
            data-testid="google-login-btn"
            className="mt-10 inline-flex items-center gap-3 villa-btn-primary px-7 py-4 rounded-full text-base shadow-[0_10px_30px_rgba(194,94,62,0.25)]"
          >
            Sign in with Google
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="mt-8 flex items-center gap-3 max-w-md">
            <div className="flex-1 h-px bg-[#E8E1D5]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A]">o</span>
            <div className="flex-1 h-px bg-[#E8E1D5]" />
          </div>

          {sent ? (
            <div className="mt-6 max-w-md p-5 rounded-2xl bg-[#FBF7EF] border border-[#E8E1D5]" data-testid="magic-link-sent">
              <p className="text-sm text-[#2B2B2B] font-serif-villa text-lg">¡Listo!</p>
              <p className="text-sm text-[#7A7A7A] mt-1">If <span className="text-[#2B2B2B]">{sent}</span> is invited, the magic link is on its way. It expires in 30 minutes.</p>
              <button onClick={() => { setSent(null); setEmail(""); }} className="text-xs text-[#C25E3E] hover:underline mt-3" data-testid="magic-link-resend">Use a different email →</button>
            </div>
          ) : (
            <form onSubmit={handleMagicLink} className="mt-6 max-w-md" data-testid="magic-link-form">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">Or sign in with a magic link</span>
                <div className="mt-2 flex gap-2">
                  <input
                    type="email"
                    required
                    placeholder="you@email.com"
                    data-testid="magic-link-email"
                    className="flex-1 bg-[#FDFBF7] border border-[#E8E1D5] rounded-full px-5 py-3 text-[#2B2B2B] placeholder:text-[#B5B5B5] focus:outline-none focus:ring-2 focus:ring-[#C25E3E]/40 focus:border-[#C25E3E] transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="magic-link-send"
                    className="px-5 py-3 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Send link
                  </button>
                </div>
              </label>
            </form>
          )}

          {error && (
            <div className="mt-5 rounded-2xl bg-[#FBE9E7] border border-[#F2C9B6] text-[#A84C30] text-sm p-4 max-w-md" data-testid="login-error">
              {error}
            </div>
          )}

          <p className="mt-6 text-sm text-[#9A9A9A]">
            Private use — only invited family members.
          </p>
        </div>

        <div className="text-xs text-[#9A9A9A] tracking-wider">
          La Manga Club · Murcia · España
        </div>
      </div>

      {/* Right – hero carousel */}
      <div className="relative hidden lg:block bg-[#1f1410]" data-testid="hero-carousel">
        {HERO_IMAGES.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.caption}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-out"
            style={{ opacity: i === slide ? 1 : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2B2B2B]/45 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-10 left-10 right-10 text-[#FDFBF7]">
          <p className="text-sm uppercase tracking-[0.25em] opacity-80">Nuestra Casa</p>
          <p className="font-serif-villa text-3xl mt-2 max-w-md leading-tight transition-opacity duration-700" key={slide}>
            {HERO_IMAGES[slide].caption}
          </p>
          <div className="flex gap-1.5 mt-5" data-testid="hero-dots">
            {HERO_IMAGES.map((img, i) => (
              <button
                key={img.src}
                type="button"
                aria-label={`Show photo ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                onClick={() => setSlide(i)}
                className={`h-1 rounded-full transition-all ${i === slide ? "w-8 bg-[#FDFBF7]" : "w-3 bg-[#FDFBF7]/40 hover:bg-[#FDFBF7]/70"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
